import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppShell } from './components/AppShell';
import { AuthProvider } from './components/AuthProvider';
import { ServicesProvider } from './components/ServicesProvider';
import { ThemeProvider } from './components/ThemeProvider';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { MfaPage } from './pages/MfaPage';
import { NovaSenhaPage } from './pages/NovaSenhaPage';
import { CadastroMfaPage } from './pages/CadastroMfaPage';
import { RecuperarSenhaPage } from './pages/RecuperarSenhaPage';
import { CargasPage } from './pages/CargasPage';
import { BasesReferenciaPage } from './pages/BasesReferenciaPage';
import { EscolasPage } from './pages/EscolasPage';
import { EscolaFichaPage } from './pages/EscolaFichaPage';
import { VisitasPage } from './pages/VisitasPage';
import { VisitaFichaPage } from './pages/VisitaFichaPage';
import { PastasPage } from './pages/PastasPage';
import { ExportacoesPage } from './pages/ExportacoesPage';
import { PainelPage } from './pages/PainelPage';
import { MapaPage } from './pages/MapaPage';
import { QualidadePage } from './pages/QualidadePage';
import { ReprocessamentoPage } from './pages/ReprocessamentoPage';
import { ReprocessamentosPage } from './pages/ReprocessamentosPage';
import { ReprocessamentoAcompanhamentoPage } from './pages/ReprocessamentoAcompanhamentoPage';
import { LotesPage } from './pages/LotesPage';
import { WhiteLabelPage } from './pages/WhiteLabelPage';
import { UsuariosPage } from './pages/UsuariosPage';
import { AuditoriaPage } from './pages/AuditoriaPage';
import { loadRuntimeConfig } from './config/runtime-config';
import type { CognitoRuntimeConfig } from './config/runtime-config';
import type { UserProfile } from './contexts/AuthContext';

/**
 * App — composição raiz do SPA: providers (React Query, auth, serviços, tema,
 * roteador) e as rotas.
 *
 * Fluxo de auth (RF-M11-001): `/login`, `/mfa`, `/nova-senha`, `/cadastro-mfa`,
 * `/recuperar-senha` são PÚBLICAS
 * e renderizam fora do AppShell. As demais rotas exigem sessão — o
 * `ProtectedRoute` redireciona ao `/login` quando anônimo (RN-M1-002, PC-04).
 * As rotas de administração exigem grupo `admin` (RN-M10-001/002, RN-M2-001).
 *
 * `VITE_API_URL` e a config do Cognito são lidas no boot; a ausência FALHA ALTO
 * (US-11 AC5) — sem default, pois apontaria para recursos inexistentes.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

export interface AppProps {
  /** Sessão inicial (usada em dev/testes; em produção o M11 resolve via Cognito). */
  readonly sessaoInicial?: {
    readonly sub: string;
    readonly email?: string;
    readonly profiles: readonly UserProfile[];
    readonly token: string;
    readonly mfaVerified?: boolean;
  } | null;
}

/** Rotas de leitura universal (consulta/editor/admin) exigindo sessão. */
const LEITURA: readonly UserProfile[] = ['consulta', 'editor', 'admin'];

export function App({ sessaoInicial = null }: AppProps): JSX.Element {
  const { apiUrl, cognito } = loadRuntimeConfig() as {
    apiUrl: string;
    cognito: CognitoRuntimeConfig;
  };

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider apiBaseUrl={apiUrl} cognitoConfig={cognito} sessaoInicial={sessaoInicial}>
        <ServicesProvider apiBaseUrl={apiUrl}>
          <ThemeProvider>
            <BrowserRouter>
              <Routes>
                {/* Rotas públicas de autenticação — sem AppShell. */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/mfa" element={<MfaPage />} />
                <Route path="/nova-senha" element={<NovaSenhaPage />} />
                <Route path="/cadastro-mfa" element={<CadastroMfaPage />} />
                <Route path="/recuperar-senha" element={<RecuperarSenhaPage />} />

                {/* Rotas autenticadas — dentro do AppShell (topbar/nav). */}
                <Route
                  path="/*"
                  element={
                    <AppShell>
                      <Routes>
                        {/* M2 — Cargas: só admin (RN-M2-001). */}
                        <Route
                          path="/cargas"
                          element={
                            <ProtectedRoute allow={['admin']}>
                              <CargasPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* M3 — Bases de Referência: só admin. */}
                        <Route
                          path="/bases"
                          element={
                            <ProtectedRoute allow={['admin']}>
                              <BasesReferenciaPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* M7 — Consulta: leitura universal (RN-M7-001), mas exige sessão. */}
                        <Route
                          path="/escolas"
                          element={
                            <ProtectedRoute allow={LEITURA}>
                              <EscolasPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/escolas/:inep"
                          element={
                            <ProtectedRoute allow={LEITURA}>
                              <EscolaFichaPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/visitas"
                          element={
                            <ProtectedRoute allow={LEITURA}>
                              <VisitasPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/visitas/:id"
                          element={
                            <ProtectedRoute allow={LEITURA}>
                              <VisitaFichaPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/pastas"
                          element={
                            <ProtectedRoute allow={LEITURA}>
                              <PastasPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/pastas/*"
                          element={
                            <ProtectedRoute allow={LEITURA}>
                              <PastasPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* M9 — Exportação: leitura universal. */}
                        <Route
                          path="/exportacoes"
                          element={
                            <ProtectedRoute allow={LEITURA}>
                              <ExportacoesPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* M10 — Painel: leitura universal. */}
                        <Route
                          path="/painel"
                          element={
                            <ProtectedRoute allow={LEITURA}>
                              <PainelPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/painel/mapa"
                          element={
                            <ProtectedRoute allow={LEITURA}>
                              <MapaPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/painel/qualidade"
                          element={
                            <ProtectedRoute allow={LEITURA}>
                              <QualidadePage />
                            </ProtectedRoute>
                          }
                        />
                        {/* M10/M11 — Administração: só admin (RN-M10-001/002, PC-06). */}
                        <Route
                          path="/admin/reprocessar"
                          element={
                            <ProtectedRoute allow={['admin']}>
                              <ReprocessamentoPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* M10/M11 — Reprocessamentos recentes e o acompanhamento (ADR-036). */}
                        <Route
                          path="/reprocessamentos"
                          element={
                            <ProtectedRoute allow={['admin']}>
                              <ReprocessamentosPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/reprocessamentos/:id"
                          element={
                            <ProtectedRoute allow={['admin']}>
                              <ReprocessamentoAcompanhamentoPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* M10 — Lotes de extração e custo (RF-M10-005, RN-M6-003/005). */}
                        <Route
                          path="/admin/lotes"
                          element={
                            <ProtectedRoute allow={['admin']}>
                              <LotesPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/white-label"
                          element={
                            <ProtectedRoute allow={['admin']}>
                              <WhiteLabelPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* M11 — Gestão de usuários (RF-M11-012, RF-AUT-04). */}
                        <Route
                          path="/admin/usuarios"
                          element={
                            <ProtectedRoute allow={['admin']}>
                              <UsuariosPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* M11 — Auditoria (RF-ADM-02, RN-GLOBAL-003). */}
                        <Route
                          path="/admin/auditoria"
                          element={
                            <ProtectedRoute allow={['admin']}>
                              <AuditoriaPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="*" element={<Navigate to="/painel" replace />} />
                      </Routes>
                    </AppShell>
                  }
                />
              </Routes>
            </BrowserRouter>
          </ThemeProvider>
        </ServicesProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
